import type { ConnectionAccountRow, ConnectionAccountStatusFilter } from '@/lib/db-tenant'
import type { Facility, Pack } from '@/types/banking'

/**
 * Pure helpers + copy for the DCP-324 connection-management screens.
 *
 * Everything here is deliberately free of React and of Supabase so the rules
 * the ACs actually specify — the initial-import date default/floor (AC#9), the
 * pack→facility filter narrowing (AC#12), the credit-card refusal (AC#15) —
 * can be tested without rendering a table or mocking a query client.
 */

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * Verbatim copy from the DCP-324 prototype (`Banking Settings` mock). Kept in
 * one object so the strings the BA signed off on are greppable and cannot drift
 * between the table header, the tooltip and the tests that assert them.
 */
export const CONNECTION_COPY = {
  /** Page heading shared by Screen 1 and the per-connection Screen 2. */
  pageTitle: 'Financial Accounts Settings',
  pageSubtitle: 'Overview of connected and manual financial accounts across all facilities',

  /** Section header of Screen 1. */
  sectionTitle: 'Connections',
  sectionSubtitle: 'Connect a financial account to import balances and transactions',
  emptyTitle: 'No financial accounts connected yet',
  emptyDescription: 'Connect a financial account to start importing transactions',
  /** Empty list on a facility's page; see {@link facilityConnectionsEmptyDescription}. */
  facilityEmptyTitle: 'No financial accounts connected for this facility',
  /** Fallback when a connection has neither a custom nor a provider name. */
  unknownInstitution: 'Unknown institution',

  /** Column-header hint on the accounts table's Type column. */
  typeHint:
    "Double-check that the account type is set correctly for each account. The type determines how the account's banking data is displayed and handled throughout the application. The API badge marks the type the institution reported — your own choice is kept and is not overwritten by the daily refresh.",
  /** Column-header hint on the accounts table's Facility column. */
  facilityHint: 'Assign a facility to account to view its imported bank transactions.',
  /** Column-header hint on the accounts table's Include in Cash Position column. */
  cashPositionHint:
    'Accounts enabled here appear in the Bank Balances list and their balances are included in the Cash Position Summary.',
  /** Tooltip on the DISABLED Include-in-Cash-Position toggle of a credit card (AC#15). */
  cashPositionCreditCard: 'Credit cards cannot be included in Cash Position',

  /** Rename dialog (AC#5). */
  renameTitle: 'Rename connection',
  renameDescription: 'This name is used across DCP wherever this institution appears.',
  renameLabel: 'Connection name',
  renamePlaceholder: 'e.g. CIBC U.S. - Business',

  /** Delete-connection confirmation (AC#7). */
  deleteConnectionCta: 'Delete connection',
  deleteConnectionDescription:
    'This connection with all accounts & cards included will be deleted. Data import will be stopped. Previously imported transactions are kept. This action cannot be undone.',

  /**
   * Account-level confirmations (AC#16/AC#17/AC#18). The singular variants are
   * for a row's kebab, the plural ones for the bulk bar — the prototype words
   * the two differently.
   */
  disconnectAccountDescription:
    'Disconnecting will stop syncing this account’s data via the integration. Previously imported data is kept and the account can be reconnected later.',
  disconnectAccountsDescription:
    'Disconnecting will stop syncing these accounts’ data via the integration. Previously imported data is kept and the accounts can be reconnected later.',
  reconnectAccountsDescription: 'Syncing will resume for this account on the next integration run.',
  deleteAccountDescription:
    'This will permanently delete the account and all its previously imported data. This action cannot be undone.',
  deleteAccountsDescription:
    'This will permanently delete the selected accounts and all their previously imported data. This action cannot be undone.',

  /** Accounts-table toolbar. */
  searchPlaceholder: 'Search name or account number',
  accountsEmptyFiltered: 'No accounts or cards match your search or filter.',
  accountsEmpty: 'This institution has no accounts or cards yet.',
  clearFilters: 'Clear filters',

  /** "Add account to {Facility}" picker. */
  addToFacilityCta: 'Add account or card',
  addToFacilityConfirm: 'Add to facility',
  addToFacilityNoneSelected: 'Select at least one account',
  addToFacilityEmptyTitle: 'No unassigned accounts',
  addToFacilityEmptyDescription: 'Every account on this bank is already assigned to a facility.',
  addToFacilityNoMatchTitle: 'No matching accounts',
  addToFacilityNoMatchDescription: 'Try a different name or account number.',
} as const

/**
 * Empty-list description on a facility's page.
 *
 * @param facilityName - The facility the page is for
 */
export function facilityConnectionsEmptyDescription(facilityName: string): string {
  return `Connect a financial account to start importing transactions for ${facilityName}.`
}

/**
 * Count of a connection's children (bank accounts and cards together) for the
 * connection header. The children are never called bare "accounts" as a level,
 * and "1 account & card" would read as two things, so the singular is
 * "account or card".
 *
 * @param count - Number of accounts and cards under the connection
 * @returns `1 account or card`, otherwise `N accounts & cards`
 */
export function accountsAndCardsLabel(count: number): string {
  return count === 1 ? '1 account or card' : `${count} accounts & cards`
}

/**
 * The AC#9 hint, with the connection's oldest available transaction date
 * interpolated. Copy is verbatim from the prototype (`startHintText`).
 *
 * @param oldestDisplayDate - Oldest selectable date, already formatted for display
 * @returns The full hint sentence
 */
export function initialImportHint(oldestDisplayDate: string): string {
  return (
    'Only transactions from this date to today will be synced during the first import. ' +
    'This applies to the initial sync only. The earliest date you can select is ' +
    oldestDisplayDate +
    ', as the maximum history available depends on the banking data provider.'
  )
}

// ---------------------------------------------------------------------------
// Account number
// ---------------------------------------------------------------------------

/** Placeholder for a value the provider did not give us. */
export const EM_DASH = '—'

/**
 * Render an account number for the table.
 *
 * LedgerSync hands us at most a 4-digit mask, and most CREDIT-CARD accounts
 * carry no number at all — so the absent case is the common one, not an edge
 * case, and it renders as an em dash rather than as an empty cell or a bare
 * `••••`. When a number IS present only its last four digits are shown.
 *
 * DIVERGES FROM `maskAccountNumber` (@/lib/utils) ON PURPOSE — and the two
 * should be reconciled. That helper renders `****1234` and is what the Banking
 * Dashboard, Manual Transactions and the manual-account section already show;
 * these screens render the prototype's `••••1234` instead, so the app now masks
 * account numbers two ways. It also has no absent-number case, which is the
 * COMMON case here (LedgerSync sends no number for most credit cards) and is
 * why this function exists at all rather than calling that one.
 *
 * Pick one glyph and delete the other; the choice is a design call, not a
 * technical one.
 *
 * @param accountNumber - Raw (already masked) number from the provider, or null
 * @returns `••••1234`, or an em dash when there is no number
 */
export function formatAccountNumber(accountNumber: string | null): string {
  const last4 = accountNumberLast4(accountNumber)
  return last4 === null ? EM_DASH : `••••${last4}`
}

/** The last four characters of a trimmed account number, or null when there is none. */
function accountNumberLast4(accountNumber: string | null): string | null {
  const trimmed = accountNumber?.trim()
  return trimmed ? trimmed.slice(-4) : null
}

/**
 * The accessible name of an account's "Include in Cash Position" switch.
 *
 * The account name alone is not unique — two accounts may share one, and their
 * switches would then be indistinguishable to assistive tech — so the name is
 * qualified with the last four digits the row shows. Spelled "ending in" rather
 * than the visible `••••` glyphs, which screen readers read aloud as "bullet".
 *
 * @param accountName - The account's display name
 * @param accountNumber - Raw (already masked) number, or null
 * @returns `Include Operating ending in 6789 in Cash Position`, or
 *   `Include Operating in Cash Position` when there is no number
 */
export function cashPositionSwitchLabel(accountName: string, accountNumber: string | null): string {
  const last4 = accountNumberLast4(accountNumber)
  return last4 === null
    ? `Include ${accountName} in Cash Position`
    : `Include ${accountName} ending in ${last4} in Cash Position`
}

/**
 * How a single-account confirmation names its account (DCP-404).
 *
 * The prototype's `Disconnect account ••••1234?`: the masked number when there
 * is one, otherwise the account's name — most LedgerSync credit cards carry no
 * number, and an em dash in a title would name nothing.
 *
 * @param account - The row the kebab action was opened from
 * @returns `••••1234`, or the account name when the provider gave no number
 */
export function accountConfirmLabel(
  account: Pick<ConnectionAccountRow, 'name' | 'account_number'>,
): string {
  return account.account_number?.trim() ? formatAccountNumber(account.account_number) : account.name
}

// ---------------------------------------------------------------------------
// Account type
// ---------------------------------------------------------------------------

/** The `account_type` enum, as it arrives on a row. */
type AccountType = ConnectionAccountRow['account_type']

/**
 * The five types the prototype offers, in its order (Credit Card first).
 *
 * `line_of_credit` is deliberately NOT offered: the prototype's picker has five
 * entries and LOC is modelled separately in DCP (the `loc_lines` tables behind
 * LOC Availability), so offering it here would invite a classification the rest
 * of the product does not read. Rows that ALREADY carry it stay renderable —
 * see {@link accountTypeOptions}.
 */
/** Display label per enum value, including the types the picker does not offer. */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  credit_card: 'Credit Card',
  checking: 'Checking',
  savings: 'Savings',
  loan: 'Loan',
  other: 'Other',
  line_of_credit: 'Line of Credit',
}

/**
 * The five types AC#13 offers in the Type picker, in the AC's order.
 *
 * Derived from {@link ACCOUNT_TYPE_LABELS} rather than restating the labels:
 * they were written out twice, so renaming a type meant editing two places and
 * a miss would have shown one label in the picker and another in the cell.
 * `line_of_credit` is excluded — it is a legacy value we still DISPLAY but never
 * offer (see {@link accountTypeOptions}).
 */
export const ACCOUNT_TYPE_CHOICES: ReadonlyArray<{ value: AccountType; label: string }> = (
  ['credit_card', 'checking', 'savings', 'loan', 'other'] as const
).map((value) => ({ value, label: ACCOUNT_TYPE_LABELS[value] }))

/**
 * Options for one row's Type select.
 *
 * Returns the five standard choices, PLUS the row's current type when that is
 * not among them (today only `line_of_credit`). Without that addition a select
 * bound to such a row would render blank and the first interaction would
 * silently reclassify the account — the user would have no way to see what it
 * was, let alone leave it alone.
 *
 * Each option also reports whether it is the type the PROVIDER last said this
 * account was (`isApi`), so the select can badge it. Since DCP-324 the daily
 * refresh no longer overwrites `account_type` — the user owns it (AC#13) — which
 * is exactly why the provider's own answer has to be visible somewhere: without
 * it, a user who set the type has no way to see that the bank disagrees, and no
 * way to go back to what the bank says.
 *
 * `apiType` null (the provider never reported one) badges nothing. It is
 * compared against the option VALUE, so a provider answer outside the standard
 * five badges the current-type row appended below when they coincide, and
 * nothing otherwise — never an option that is not on screen.
 *
 * @param current - The row's current `account_type`
 * @param apiType - The type the feed last reported, or null
 * @returns Select options, current type guaranteed present
 */
export function accountTypeOptions(
  current: AccountType,
  apiType: AccountType | null = null,
): Array<{ value: string; label: string; isApi: boolean }> {
  const options = ACCOUNT_TYPE_CHOICES.map((choice) => ({
    value: choice.value as string,
    label: choice.label,
    isApi: choice.value === apiType,
  }))
  if (!ACCOUNT_TYPE_CHOICES.some((choice) => choice.value === current)) {
    options.push({
      value: current,
      label: ACCOUNT_TYPE_LABELS[current] ?? current,
      isApi: current === apiType,
    })
  }
  return options
}

/**
 * Whether an account may be included in Cash Position (AC#15).
 *
 * A credit card's balance is what the tenant OWES, so counting it as cash on
 * hand overstates the position (the DCP-371 exclusion). The toggle is therefore
 * disabled rather than merely ignored, and the data layer refuses the write as
 * well — this predicate is only what keeps the UI honest about it.
 *
 * @param account - A connected or a manual account row
 * @returns True when the Include-in-Cash-Position toggle must be disabled
 */
export function isCashPositionLocked(account: { account_type: string | null }): boolean {
  return account.account_type === 'credit_card'
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/**
 * Sentinel option values for the two multi-selects.
 *
 * "Unassigned" and "No Pack" are real, selectable states — an account starts
 * life with no facility — but neither has a uuid to be named by. These sentinels
 * carry them through `MultiSelect` (whose value is `string[]`) and are mapped
 * back to a literal `null` ENTRY before the query, which is exactly what the
 * RPC matches with `array_position(..., NULL)`.
 *
 * Prefixed and bracketed so they can never collide with a real uuid.
 */
export const UNASSIGNED_FACILITY = '__unassigned__'
export const NO_PACK = '__no_pack__'

/** Visible labels for the two sentinels. */
export const UNASSIGNED_FACILITY_LABEL = 'Unassigned'
export const NO_PACK_LABEL = 'No Pack'

/**
 * Convert a MultiSelect value into the `(string | null)[]` the data layer wants.
 *
 * Returns `undefined` for an empty selection, which the data layer reads as "no
 * filter" — the house convention. An EMPTY array would mean "the caller resolved
 * zero rows" and is never what an untouched filter means.
 *
 * @param selected - Raw MultiSelect values, possibly containing a sentinel
 * @param sentinel - The sentinel standing for the null bucket
 * @returns Filter entries with the sentinel replaced by null, or undefined
 */
export function toFilterEntries(
  selected: string[],
  sentinel: string,
): (string | null)[] | undefined {
  if (selected.length === 0) return undefined
  return selected.map((value) => (value === sentinel ? null : value))
}

/**
 * Options for the Pack filter: every pack, plus "No Pack".
 *
 * @param packs - The org's packs
 * @returns MultiSelect options
 */
export function packFilterOptions(packs: Pack[]): Array<{ value: string; label: string }> {
  return [
    ...packs.map((pack) => ({ value: pack.id, label: pack.name })),
    { value: NO_PACK, label: NO_PACK_LABEL },
  ]
}

/**
 * The facilities a given Pack selection admits.
 *
 * ONE definition for each page's two uses that must never disagree: the Facility
 * dropdown's options and the pruning that runs when the Pack selection changes.
 * A rule that lives in two copies drifts — add a second sentinel pack and fix
 * only one copy, and the dropdown offers one set while the applied filter uses
 * another, which shows up as an empty table with every control saying "All".
 *
 * An empty selection means "no pack filter", i.e. every active facility. The
 * NO_PACK sentinel selects the facilities that belong to no pack. Unlike
 * {@link facilityFilterOptions} it offers no "Unassigned" entry: for pages
 * whose rows always have a facility (Credit Card Availability, Cash Position).
 *
 * @param activeFacilities - Already scoped to `is_active`
 * @param packSelection - Pack ids, possibly including the NO_PACK sentinel
 * @returns The subset the selection admits, in the order given
 */
export function facilitiesForPackSelection<T extends { pack_id: string | null }>(
  activeFacilities: T[],
  packSelection: string[],
): T[] {
  if (packSelection.length === 0) return activeFacilities
  const packIds = new Set(packSelection.filter((value) => value !== NO_PACK))
  const wantsPackless = packSelection.includes(NO_PACK)
  return activeFacilities.filter((f) =>
    f.pack_id == null ? wantsPackless : packIds.has(f.pack_id),
  )
}

/**
 * Options for the Facility filter, narrowed by the Pack selection (AC#12).
 *
 * With no pack selected the filter offers every active facility plus
 * "Unassigned". Once packs ARE selected it offers only the facilities of those
 * packs — anything else could not match a row that already passed the pack
 * filter, so offering it would produce a combination that is always empty.
 *
 * "No Pack" stands for every account whose facility has no pack — so it offers
 * the facilities with `pack_id` NULL, and "Unassigned" too: an account with no
 * facility has no pack either, so it can only be reached through that bucket.
 *
 * @param facilities - Active facilities (each carrying its `pack_id`)
 * @param selectedPacks - Raw Pack-filter values, possibly containing {@link NO_PACK}
 * @returns MultiSelect options for the Facility filter
 */
export function facilityFilterOptions(
  facilities: Facility[],
  selectedPacks: string[],
): Array<{ value: string; label: string }> {
  const unassigned = { value: UNASSIGNED_FACILITY, label: UNASSIGNED_FACILITY_LABEL }
  if (selectedPacks.length === 0) {
    return [...facilities.map((f) => ({ value: f.id, label: f.name })), unassigned]
  }
  const options = facilitiesForPackSelection(facilities, selectedPacks).map((f) => ({
    value: f.id,
    label: f.name,
  }))
  if (selectedPacks.includes(NO_PACK)) options.push(unassigned)
  return options
}

/**
 * Drop facility selections the narrowed option list no longer offers (AC#12).
 *
 * Called when the Pack selection changes. Without it a facility picked BEFORE a
 * pack was chosen would stay in the query while disappearing from the dropdown
 * — an invisible filter the user cannot clear, whose usual symptom is an empty
 * table with every visible control saying "All".
 *
 * @param selectedFacilities - Current Facility-filter values
 * @param options - The narrowed option list from {@link facilityFilterOptions}
 * @returns The subset still offered, in its original order
 */
export function pruneFacilitySelection(
  selectedFacilities: string[],
  options: Array<{ value: string }>,
): string[] {
  const allowed = new Set(options.map((option) => option.value))
  return selectedFacilities.filter((value) => allowed.has(value))
}

/** The table's facility scope and Facility / Pack filters, as {@link facilityKeepsAccountShown} reads them. */
export interface AccountFacilityFilters {
  /** The facility page's facility; null org-wide. */
  scopeFacilityId: string | null
  /** Raw Facility-filter values; may contain {@link UNASSIGNED_FACILITY}. */
  facilityFilter: string[]
  /** Raw Pack-filter values; may contain {@link NO_PACK}. */
  packFilter: string[]
  /** Every facility, inactive included, each carrying its `pack_id`. */
  facilities: Facility[]
}

/**
 * Whether an account moved to `facilityId` still matches the facility scope and
 * the Facility / Pack filters, by the rules of `get_connection_accounts`: the
 * pack is the facility's, and an unassigned account is both "Unassigned" and
 * "No Pack". Search does not read the facility, so it is not consulted. A
 * facility missing from `facilities` counts as not matching — the caller drops
 * the row from the selection, the safe side.
 *
 * @param facilityId - The new facility; null for Unassigned
 * @param filters - The table's scope and filters
 */
export function facilityKeepsAccountShown(
  facilityId: string | null,
  { scopeFacilityId, facilityFilter, packFilter, facilities }: AccountFacilityFilters,
): boolean {
  if (scopeFacilityId !== null) return facilityId === scopeFacilityId
  if (facilityFilter.length > 0 && !facilityFilter.includes(facilityId ?? UNASSIGNED_FACILITY)) return false
  if (packFilter.length === 0) return true
  if (facilityId === null) return packFilter.includes(NO_PACK)
  const facility = facilities.find((f) => f.id === facilityId)
  if (!facility) return false
  return packFilter.includes(facility.pack_id ?? NO_PACK)
}

/** The three account-status filter choices (AC#10); `connected` is the default. */
export const ACCOUNT_STATUS_OPTIONS: ReadonlyArray<{
  value: ConnectionAccountStatusFilter
  label: string
}> = [
  { value: 'connected', label: 'Connected' },
  { value: 'disconnected', label: 'Disconnected' },
  { value: 'all', label: 'All' },
]

// ---------------------------------------------------------------------------
// Initial-import date (AC#9 / AC#23)
// ---------------------------------------------------------------------------

/**
 * RE-EXPORTED, NOT DEFINED HERE. The rule lives in `@dcp/shared` because the
 * sync task needs the SAME answer: `initial_import_from` is the `from` of a
 * connection's first pull, so a second copy of this arithmetic is how the
 * screen came to advertise a bound the sync did not apply (it showed
 * 01/01/2026 while the importer pulled everything back to 2026-06-13).
 *
 * Kept re-exported from this module so the screens keep importing their rules
 * from one place.
 */
export type { InitialImportDateState } from '@dcp/shared/utils/initial-import-date'
export {
  resolveInitialImportDate,
  oldestTransactionDate,
} from '@dcp/shared/utils/initial-import-date'

/**
 * Up to two initials for a connection's avatar, mirroring the prototype's
 * `initials()`.
 *
 * Splits on whitespace and takes the first letter of the first two words, so
 * "CIBC U.S. - Business" reads "CU" rather than "CI". Non-letter tokens (the
 * "-" in that name, "&") are dropped first, otherwise a separator would win a
 * slot. Returns an empty string when nothing qualifies, letting the caller
 * decide the fallback.
 *
 * @param name - The connection's display name
 * @returns One or two uppercase letters, or an empty string
 */
export function bankInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('')
}
